import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtDateTime } from '@/lib/format-date';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';

/**
 * Recent inbound API-key requests.
 *
 * The support surface for "our integration isn't working". Before this
 * existed the only evidence was the audit trail, which covers writes and
 * nothing else — so a partner whose key was revoked, or who was being
 * rejected on every call, showed up nowhere at all.
 *
 * Failures are what people come here for, so the Errors filter is one click
 * and failed rows are visually distinct.
 */

interface RequestRow {
  id: string;
  method: string;
  path: string;
  query: string | null;
  status: number;
  durationMs: number;
  apiKeyId: string | null;
  apiKeyName: string | null;
  keyLast4: string | null;
  ip: string | null;
  requestBody: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: string;
}

interface RequestsResponse {
  data: RequestRow[];
  meta: { count: number; limit: number; retentionDays: number };
}

function StatusPill({ status }: { status: number }) {
  const tone = status >= 500 ? 'bg-destructive/10 text-destructive border-destructive/30'
    : status >= 400 ? 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-500 border-amber-300 dark:border-amber-900'
      : 'bg-muted/60 text-muted-foreground border-border';
  return (
    <span className={`inline-block font-mono text-[11px] px-1.5 py-0.5 rounded border tabular-nums ${tone}`}>
      {status}
    </span>
  );
}

/** Pretty-print if it parses; show it raw if it doesn't. A body we couldn't
 *  parse is still evidence — refusing to display it would hide the very case
 *  someone is investigating. */
function formatBody(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export function DeveloperRequestsPanel({ keys }: { keys: Array<{ id: string; name: string }> }) {
  const [errorsOnly, setErrorsOnly] = React.useState(false);
  const [keyId, setKeyId] = React.useState('');
  const [openBody, setOpenBody] = React.useState<string | null>(null);

  const params = new URLSearchParams({ limit: '100' });
  if (errorsOnly) params.set('errorsOnly', 'true');
  if (keyId) params.set('keyId', keyId);

  /**
   * Closed by default, and the query is gated on it. This endpoint returns up
   * to 100 rows each carrying a request payload, so fetching it on every visit
   * to the page would be paid for by everyone to serve the rare occasion
   * someone is actually debugging an integration.
   */
  const [open, setOpen] = React.useState(false);

  const { data, isFetching, refetch, error } = useQuery<RequestsResponse>({
    enabled: open,
    queryKey: ['/api-keys/requests', errorsOnly, keyId],
    queryFn: async () => {
      const r = await api<RequestRow[]>(`/api-keys/requests?${params.toString()}`);
      // Throw rather than falling back to an empty list. Returning [] on a
      // failed request makes a broken endpoint look identical to a quiet
      // one — "no requests recorded yet" when the truth is 403, or 404
      // because the server predates this route.
      if (!r.success) throw new Error(r.error?.message || 'Failed to load requests');
      return { data: r.data || [], meta: (r as any).meta ?? { count: 0, limit: 100, retentionDays: 30 } };
    },
    retry: false,
  });

  const rows = data?.data ?? [];
  const retention = data?.meta?.retentionDays ?? 30;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div
            role="button"
            tabIndex={0}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); }
            }}
            className="flex items-start gap-2 cursor-pointer select-none group min-w-0"
          >
            {open
              ? <ChevronDown className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0 group-hover:text-foreground" />
              : <ChevronRight className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0 group-hover:text-foreground" />}
            <div className="min-w-0">
              <CardTitle className="text-base">Recent API requests</CardTitle>
              <CardDescription>
                Every call made with an API key, including ones that were rejected. Kept for {retention} days.
              </CardDescription>
            </div>
          </div>
          {/* Refresh only makes sense against visible data. */}
          {open && (
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          )}
        </div>
      </CardHeader>
      {open && (
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <Button size="sm" variant={errorsOnly ? 'outline' : 'default'} onClick={() => setErrorsOnly(false)}>
            All
          </Button>
          <Button size="sm" variant={errorsOnly ? 'default' : 'outline'} onClick={() => setErrorsOnly(true)}>
            Errors only
          </Button>
          {keys.length > 0 && (
            <select
              value={keyId}
              onChange={(e) => setKeyId(e.target.value)}
              className="h-8 rounded-md border bg-background px-2 text-sm"
              aria-label="Filter by key"
            >
              <option value="">All keys</option>
              {keys.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
            </select>
          )}
        </div>

        {error && (
          <div className="border rounded-md px-3 py-3 text-sm border-destructive/40 bg-destructive/5">
            <span className="font-medium text-destructive">Couldn&rsquo;t load requests.</span>{' '}
            <span className="text-muted-foreground">{(error as Error).message}</span>
          </div>
        )}

        {!error && rows.length === 0 && (
          <div className="border rounded-md px-3 py-3 text-sm text-muted-foreground">
            {isFetching ? 'Loading…'
              : errorsOnly || keyId ? 'No requests match this filter.'
                : 'No API requests recorded yet. They appear here as soon as an integration calls the API.'}
          </div>
        )}

        {/* Cards on small screens, table on large — the dual render used across the app. */}
        {rows.length > 0 && (
          <div className="lg:hidden space-y-2">
            {rows.map((r) => (
              <div key={r.id} className="border rounded-md p-3 text-xs space-y-1">
                <div className="flex items-center gap-2">
                  <StatusPill status={r.status} />
                  <span className="font-mono font-medium">{r.method}</span>
                  <span className="ml-auto text-muted-foreground">{fmtDateTime(r.createdAt)}</span>
                </div>
                <div className="font-mono break-all">{r.path}{r.query ? `?${r.query}` : ''}</div>
                <div className="text-muted-foreground">
                  {r.apiKeyName ?? 'unrecognised key'} {r.keyLast4 && <span className="font-mono">{r.keyLast4}</span>}
                  {' · '}{r.durationMs}ms
                </div>
                {r.errorCode && (
                  <div className="text-amber-700 dark:text-amber-500">
                    <span className="font-mono">{r.errorCode}</span>
                    {r.errorMessage ? ` — ${r.errorMessage}` : ''}
                  </div>
                )}
                {r.requestBody && (
                  <div>
                    <button
                      type="button"
                      onClick={() => setOpenBody(openBody === r.id ? null : r.id)}
                      className="text-primary hover:underline text-[11px]"
                    >
                      {openBody === r.id ? 'Hide payload' : 'Show payload'}
                    </button>
                    {openBody === r.id && (
                      <pre className="mt-1 text-[10px] font-mono bg-muted/50 border rounded p-2 overflow-x-auto max-h-60 whitespace-pre">
{formatBody(r.requestBody)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {rows.length > 0 && (
          <div className="hidden lg:block border rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs">
                <tr>
                  <th className="text-left px-3 py-2 w-40">When</th>
                  <th className="text-left px-3 py-2 w-16">Status</th>
                  <th className="text-left px-3 py-2 w-24">Method</th>
                  <th className="text-left px-3 py-2">Path</th>
                  <th className="text-left px-3 py-2 w-44">Key</th>
                  <th className="text-right px-3 py-2 w-20">Time</th>
                  <th className="text-left px-3 py-2 w-64">Error</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <React.Fragment key={r.id}>
                  <tr className={`border-t align-top ${r.status >= 400 ? 'bg-amber-50/40 dark:bg-amber-950/10' : ''}`}>
                    <td className="px-3 py-1.5 text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(r.createdAt)}</td>
                    <td className="px-3 py-1.5"><StatusPill status={r.status} /></td>
                    <td className="px-3 py-1.5 font-mono text-xs">
                      {r.method}
                      {/* Only GET/DELETE lack a body, so absence here is
                          expected rather than missing data — no affordance.
                          `nowrap` because the column is narrow and "Hide
                          payload" would otherwise break across two lines. */}
                      {r.requestBody && (
                        <button
                          type="button"
                          onClick={() => setOpenBody(openBody === r.id ? null : r.id)}
                          className="block text-primary hover:underline text-[10px] font-sans mt-0.5 whitespace-nowrap"
                        >
                          {openBody === r.id ? 'Hide' : 'Payload'}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs break-all">
                      {r.path}
                      {r.query && <span className="text-muted-foreground">?{r.query}</span>}
                    </td>
                    <td className="px-3 py-1.5 text-xs">
                      {/* A key we couldn't resolve is the interesting case, not a blank. */}
                      {r.apiKeyName ?? <span className="text-muted-foreground italic">unrecognised</span>}
                      {r.keyLast4 && <span className="font-mono text-muted-foreground ml-1">{r.keyLast4}</span>}
                    </td>
                    <td className="px-3 py-1.5 text-xs text-right tabular-nums text-muted-foreground">{r.durationMs}ms</td>
                    <td className="px-3 py-1.5 text-xs">
                      {r.errorCode ? (
                        <span className="text-amber-700 dark:text-amber-500">
                          <span className="font-mono">{r.errorCode}</span>
                          {r.errorMessage && <span className="block text-muted-foreground">{r.errorMessage}</span>}
                        </span>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                  </tr>
                  {openBody === r.id && r.requestBody && (
                    <tr className="border-t-0">
                      <td colSpan={7} className="px-3 pb-3 pt-0">
                        <pre className="text-[11px] font-mono bg-muted/50 border rounded p-2 overflow-x-auto max-h-72 whitespace-pre">
{formatBody(r.requestBody)}
                        </pre>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows.length >= (data?.meta?.limit ?? 100) && (
          <p className="text-[11px] text-muted-foreground">
            Showing the most recent {data?.meta?.limit}. Narrow with the filters above to see further back.
          </p>
        )}
      </CardContent>
      )}
    </Card>
  );
}

export default DeveloperRequestsPanel;
